/**
 * Social Media Arche — API surface (Vault-resident, W8 Phase D).
 *
 * All HTTP methods this arche needs live here, callable as
 * `socialMediaApi.<method>(holder, ...)`. Every method takes `holder`
 * (a Vault Holder ID) as the first arg and threads it onto the request
 * URL as `?holder=`. This mirrors the W6 Todo Lists client rewire
 * (commit `17826064`).
 *
 * Per docs/audits/social-media-modularity-audit.md §10 PW-B (social-media
 * slice of the api.ts split).
 *
 * Convention: every method signature is `(holder: string, ...rest)`. The
 * Holder ID is bound at the call site from `useHolderSwitcher().selectedHolderId`,
 * not pulled from any per-route context, so cross-Holder leakage cannot
 * happen at the client layer.
 */

import { api } from '@/lib/api';
import type {
  SocialMediaAccountPublic,
  SocialMediaPost,
  AdaptedPlatformContent,
  SocialPlatform,
  PublishAttemptResult,
  SocialMediaPublishLogEntry,
  SocialMediaPostCounts,
  SocialMediaDailyCount,
  SocialAnalyticsRange,
  SocialAnalyticsResponse,
  InspirationPlatform,
  ContentStatus,
  UnifiedContentItem,
  SocialMediaCompetitorItem,
  SocialMediaRssFeedItem,
} from '@/lib/api';

/** Vault Holder type discriminator. Inlined since client/src/lib/api.ts
 *  uses inline `'personal' | 'org'` for the same field. */
type VaultHolderType = 'personal' | 'org';

/**
 * Adds `holder=<id>` to a query string fragment, preserving any
 * existing params. Returns the leading `?` so callers can append
 * directly to a path.
 */
function holderQs(holder: string, extra?: URLSearchParams | string): string {
  const params = extra instanceof URLSearchParams ? extra : new URLSearchParams(extra ?? '');
  params.set('holder', holder);
  return `?${params.toString()}`;
}

/** Shared brand-settings response/upsert shape. Holder-scoped after W8. */
interface BrandSettingsPayload {
  settings: {
    settingId: string | null;
    holderId: string;
    holderType: VaultHolderType;
    identity: {
      brandName: string;
      industry: string | null;
      tagline: string | null;
      website: string | null;
    };
    logo: {
      lightLogoFileId: string | null;
      darkLogoFileId: string | null;
      usage: string;
      placement: string;
    };
    palette: Array<{ name: string; hex: string }>;
    voice: {
      personaArchetype: string;
      toneSpectrum: { formality: number; humor: number; boldness: number; empathy: number };
      voiceDescriptors: string[];
      avoidDescriptors: string[];
      brandDescription: string | null;
      targetAudience: string | null;
      contentPillars: string | null;
      personaVoice: string | null;
      signOffStyle: string;
      defaultCTAType: string;
      wordsToUse: string | null;
      wordsToNeverUse: string | null;
      brandSpecificTerms: string | null;
      hashtagStrategy: string | null;
      emojiUsage: string;
      postLengthPreference: string;
      useLineBreaks: boolean;
      includeQuestions: boolean;
      referenceBrandColor: boolean;
    };
    platformOverrides: Array<{
      platform: string;
      toneNote: string | null;
      isActive: boolean;
    }>;
    createdAt: string | null;
    updatedAt: string | null;
  };
}

export const socialMediaApi = {
  // ── Accounts ────────────────────────────────────────────────────────

  listSocialMediaAccounts(holder: string) {
    return api.httpRequest<{ accounts: SocialMediaAccountPublic[] }>(
      'GET',
      `/arches/social-media/accounts${holderQs(holder)}`,
    );
  },

  initSocialMediaOAuth(holder: string, platform: string, redirectUri: string) {
    const params = new URLSearchParams({ platform, redirectUri });
    return api.httpRequest<{ authorizationUrl: string; state: string }>(
      'GET',
      `/arches/social-media/accounts/oauth/init${holderQs(holder, params)}`,
    );
  },

  disconnectSocialMediaAccount(holder: string, accountId: string) {
    return api.httpRequest<void>(
      'DELETE',
      `/arches/social-media/accounts/${accountId}${holderQs(holder)}`,
    );
  },

  searchTwitterUsers(holder: string, query: string, accountId: string, verifiedOnly = false) {
    const params = new URLSearchParams({ query, accountId });
    if (verifiedOnly) params.set('verifiedOnly', 'true');
    return api.httpRequest<{
      users: {
        id: string;
        name: string;
        username: string;
        profileImageUrl?: string;
        description?: string;
        verified: boolean;
        verifiedType: 'blue' | 'government' | 'business' | 'none';
        followersCount: number;
      }[];
    }>('GET', `/arches/social-media/accounts/twitter/users/search${holderQs(holder, params)}`);
  },

  searchLinkedInOrgs(holder: string, query: string, accountId: string) {
    const params = new URLSearchParams({ q: query, accountId });
    return api.httpRequest<{
      orgs: { id: string; name: string; urn: string }[];
    }>('GET', `/arches/social-media/accounts/linkedin/orgs/search${holderQs(holder, params)}`);
  },

  // ── Posts ───────────────────────────────────────────────────────────

  listSocialMediaPosts(
    holder: string,
    options?: {
      status?: string;
      search?: string;
      limit?: number;
      offset?: number;
      /** ISO 8601 lower bound — COALESCE(publish_at, created_at) >= from */
      from?: string;
      /** ISO 8601 upper bound — COALESCE(publish_at, created_at) <= to */
      to?: string;
    },
  ) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.search) params.set('search', options.search);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    return api.httpRequest<{ posts: SocialMediaPost[]; total: number }>(
      'GET',
      `/arches/social-media/posts${holderQs(holder, params)}`,
    );
  },

  createSocialMediaPost(
    holder: string,
    data: {
      originalContent: string;
      targetAccountIds?: string[];
      mediaFileIds?: string[];
      timezone?: string;
      title?: string;
      notes?: string;
      tags?: string[];
      link?: string;
    },
  ) {
    return api.httpRequest<SocialMediaPost>(
      'POST',
      `/arches/social-media/posts${holderQs(holder)}`,
      data,
    );
  },

  getSocialMediaPost(holder: string, postId: string) {
    return api.httpRequest<SocialMediaPost>(
      'GET',
      `/arches/social-media/posts/${postId}${holderQs(holder)}`,
    );
  },

  updateSocialMediaPost(
    holder: string,
    postId: string,
    data: {
      originalContent?: string;
      targetAccountIds?: string[];
      publishAt?: string | null;
      platformContent?: Record<string, AdaptedPlatformContent>;
      mediaFileIds?: string[];
      hasAiContent?: boolean;
      timezone?: string;
      title?: string | null;
      notes?: string | null;
      tags?: string[];
      link?: string | null;
    },
  ) {
    return api.httpRequest<SocialMediaPost>(
      'PATCH',
      `/arches/social-media/posts/${postId}${holderQs(holder)}`,
      data,
    );
  },

  deleteSocialMediaPost(holder: string, postId: string) {
    return api.httpRequest<void>(
      'DELETE',
      `/arches/social-media/posts/${postId}${holderQs(holder)}`,
    );
  },

  adaptSocialMediaPost(
    holder: string,
    postId: string,
    platforms?: string[],
    context?: {
      tone?: string[];
      goal?: string[];
      audience?: string;
      link?: string;
      currentPlatformText?: Record<string, string>;
    },
    platformSettings?: Record<string, { format?: string }>,
  ) {
    return api.httpRequest<{ platformContent: Record<string, AdaptedPlatformContent> }>(
      'POST',
      `/arches/social-media/posts/${postId}/adapt${holderQs(holder)}`,
      { platforms, context, platformSettings },
    );
  },

  enhanceTwitterThread(
    holder: string,
    postId: string,
    tweets: string[],
    context?: { tone?: string[]; goal?: string[]; audience?: string },
  ) {
    return api.httpRequest<{ tweets: string[] }>(
      'POST',
      `/arches/social-media/posts/${postId}/enhance-twitter-thread${holderQs(holder)}`,
      { tweets, context },
    );
  },

  scheduleSocialMediaPost(holder: string, postId: string, publishAt: string) {
    return api.httpRequest<SocialMediaPost>(
      'POST',
      `/arches/social-media/posts/${postId}/schedule${holderQs(holder)}`,
      { publishAt },
    );
  },

  resetSocialMediaPostToDraft(holder: string, postId: string) {
    return api.httpRequest<SocialMediaPost>(
      'POST',
      `/arches/social-media/posts/${postId}/reset-draft${holderQs(holder)}`,
    );
  },

  publishSocialMediaPostNow(holder: string, postId: string) {
    return api.httpRequest<{ results: Partial<Record<SocialPlatform, PublishAttemptResult>> }>(
      'POST',
      `/arches/social-media/posts/${postId}/publish-now${holderQs(holder)}`,
    );
  },

  getSocialMediaPostLog(holder: string, postId: string) {
    return api.httpRequest<{ log: SocialMediaPublishLogEntry[] }>(
      'GET',
      `/arches/social-media/posts/${postId}/log${holderQs(holder)}`,
    );
  },

  // ── AI helpers ──────────────────────────────────────────────────────
  //
  // Pure-prompt AI surfaces (suggestions, best-time, hashtags,
  // enhance-image-prompt) are user-billed and don't read brand
  // context — they stay holder-less. Writer surfaces (ideas, regenerate,
  // calendar) read the brand voice from the Holder, so they take
  // `holder` as the first arg.

  getSocialMediaSuggestions(content: string, platform?: string) {
    return api.httpRequest<{
      hooks: string[];
      engagementTips: string[];
      toneAnalysis: string;
    }>('POST', '/arches/social-media/ai/suggestions', { content, platform });
  },

  getSocialMediaBestTime(platform: string) {
    return api.httpRequest<{
      recommendations: Array<{ dayOfWeek: string; timeRange: string; reason: string }>;
      generalAdvice: string;
    }>('POST', '/arches/social-media/ai/best-time', { platform });
  },

  getSocialMediaHashtags(content: string, platform: string) {
    return api.httpRequest<{
      recommended: string[];
      trending: string[];
      analysis: string;
    }>('POST', '/arches/social-media/ai/hashtags', { content, platform });
  },

  enhanceImagePrompt(prompt: string) {
    return api.httpRequest<{
      enhanced: {
        fields: Array<{ label: string; value: string }>;
        clarification?: string;
      };
    }>('POST', '/arches/social-media/ai/enhance-image-prompt', { prompt });
  },

  generateWriterIdeas(
    holder: string,
    data: {
      direction: string;
      directionOverride?: string;
      audiencePain?: string[];
      audiencePainOverride?: string;
      style?: string[];
      hookPreference?: string[];
      inspirationSeed?: string;
      selectedInspirationIds?: string[];
      selectedInspirationContent?: Array<{ author: string; content: string; platform: string }>;
      constraints?: string;
      feedback?: string;
    },
  ) {
    return api.httpRequest<{
      ideas: Array<{
        hook: string;
        concept: string;
        angle: string;
        format: 'tweet' | 'thread' | 'carousel' | 'video';
        notes?: string;
        whyItWorks: string;
        pillar: string;
      }>;
    }>('POST', `/arches/social-media/ai/writer/ideas${holderQs(holder)}`, data);
  },

  regenerateWriterIdea(
    holder: string,
    data: {
      direction: string;
      pillar: string;
      audiencePain?: string[];
      style?: string[];
      hookPreference?: string[];
      constraints?: string;
    },
  ) {
    return api.httpRequest<{
      idea: {
        hook: string;
        concept: string;
        angle: string;
        format: 'tweet' | 'thread' | 'carousel' | 'video';
        notes?: string;
        whyItWorks: string;
        pillar: string;
      };
    }>('POST', `/arches/social-media/ai/writer/regenerate-idea${holderQs(holder)}`, data);
  },

  generateWriterCalendar(
    holder: string,
    data: {
      timeAmount: number;
      timeUnit: 'weeks' | 'months';
      scheduleType: 'per-period' | 'daily';
      postsPerPeriod: number;
      postsPerDay: number;
      platforms?: string[];
      seedIdeas?: string;
      selectedInspirationContent?: Array<{ author: string; content: string; platform: string }>;
      goal?: string;
      contentStyle?: string[];
      engagementStyle?: string[];
      constraints?: string;
      pillars?: Array<{ name: string; weight: number }>;
    },
  ) {
    return api.httpRequest<{
      posts: Array<{
        day: string;
        time: string;
        pillar: string;
        hook: string;
        copy: string;
        format: string;
        formatHint: string;
      }>;
    }>('POST', `/arches/social-media/ai/writer/calendar${holderQs(holder)}`, data);
  },

  // ── Stats + analytics ───────────────────────────────────────────────

  getSocialMediaStats(holder: string, month?: string) {
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    return api.httpRequest<{
      counts: SocialMediaPostCounts;
      dailyActivity: SocialMediaDailyCount[];
    }>('GET', `/arches/social-media/stats${holderQs(holder, params)}`);
  },

  getSocialMediaAnalytics(
    holder: string,
    params: { range: SocialAnalyticsRange; platform?: SocialPlatform },
  ) {
    const qs = new URLSearchParams({ range: params.range });
    if (params.platform) qs.set('platform', params.platform);
    return api.httpRequest<SocialAnalyticsResponse>(
      'GET',
      `/arches/social-media/analytics${holderQs(holder, qs)}`,
    );
  },

  // ── Inspiration content ─────────────────────────────────────────────

  listInspirationContent(
    holder: string,
    options?: {
      platform?: InspirationPlatform;
      status?: ContentStatus;
      search?: string;
      sortBy?: 'engagement' | 'fetched' | 'posted';
      sortDir?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
      includeDiscarded?: boolean;
    },
  ) {
    const params = new URLSearchParams();
    if (options?.platform) params.set('platform', options.platform);
    if (options?.status) params.set('status', options.status);
    if (options?.search) params.set('search', options.search);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortDir) params.set('sortDir', options.sortDir);
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    if (options?.includeDiscarded) params.set('includeDiscarded', 'true');
    return api.httpRequest<{ items: UnifiedContentItem[]; total: number }>(
      'GET',
      `/arches/social-media/inspiration${holderQs(holder, params)}`,
    );
  },

  updateInspirationContent(
    holder: string,
    contentId: string,
    update: { status?: ContentStatus; isFavorited?: boolean; pillarTag?: string | null },
  ) {
    return api.httpRequest<{ item: UnifiedContentItem }>(
      'PATCH',
      `/arches/social-media/inspiration/${contentId}${holderQs(holder)}`,
      update,
    );
  },

  bulkUpdateInspirationContent(
    holder: string,
    data: {
      contentIds: string[];
      action: 'status' | 'favorite' | 'unfavorite' | 'tag' | 'discard';
      status?: ContentStatus;
      pillarTag?: string | null;
    },
  ) {
    return api.httpRequest<{ updatedCount: number; draftsQueued?: number }>(
      'POST',
      `/arches/social-media/inspiration/bulk${holderQs(holder)}`,
      data,
    );
  },

  // ── Inspiration sources (competitors + RSS + manual) ────────────────

  listCompetitors(holder: string) {
    return api.httpRequest<{ competitors: SocialMediaCompetitorItem[] }>(
      'GET',
      `/arches/social-media/inspiration/sources/competitors${holderQs(holder)}`,
    );
  },

  addCompetitor(
    holder: string,
    data: { platform: InspirationPlatform; username: string; displayName?: string },
  ) {
    return api.httpRequest<{ competitor: SocialMediaCompetitorItem }>(
      'POST',
      `/arches/social-media/inspiration/sources/competitors${holderQs(holder)}`,
      data,
    );
  },

  deleteCompetitor(holder: string, competitorId: string) {
    return api.httpRequest<undefined>(
      'DELETE',
      `/arches/social-media/inspiration/sources/competitors/${competitorId}${holderQs(holder)}`,
    );
  },

  triggerCompetitorFetch(holder: string, competitorId: string) {
    return api.httpRequest<{ competitor: SocialMediaCompetitorItem; message: string }>(
      'POST',
      `/arches/social-media/inspiration/sources/competitors/${competitorId}/fetch${holderQs(holder)}`,
    );
  },

  abortCompetitorFetch(holder: string, competitorId: string) {
    return api.httpRequest<{ competitor: SocialMediaCompetitorItem }>(
      'DELETE',
      `/arches/social-media/inspiration/sources/competitors/${competitorId}/fetch${holderQs(holder)}`,
    );
  },

  listRssFeeds(holder: string) {
    return api.httpRequest<{ feeds: SocialMediaRssFeedItem[] }>(
      'GET',
      `/arches/social-media/inspiration/sources/rss${holderQs(holder)}`,
    );
  },

  addRssFeed(holder: string, data: { title: string; url: string }) {
    return api.httpRequest<{ feed: SocialMediaRssFeedItem }>(
      'POST',
      `/arches/social-media/inspiration/sources/rss${holderQs(holder)}`,
      data,
    );
  },

  deleteRssFeed(holder: string, feedId: string) {
    return api.httpRequest<undefined>(
      'DELETE',
      `/arches/social-media/inspiration/sources/rss/${feedId}${holderQs(holder)}`,
    );
  },

  triggerRssFeedFetch(holder: string, feedId: string) {
    return api.httpRequest<{ feed: SocialMediaRssFeedItem; message: string }>(
      'POST',
      `/arches/social-media/inspiration/sources/rss/${feedId}/fetch${holderQs(holder)}`,
    );
  },

  fetchManualInspirationUrl(holder: string, url: string) {
    const params = new URLSearchParams({ url });
    return api.httpRequest<{ text: string }>(
      'GET',
      `/arches/social-media/inspiration/sources/manual/fetch-url${holderQs(holder, params)}`,
    );
  },

  addManualInspiration(holder: string, data: { text: string; title?: string; sourceUrl?: string }) {
    return api.httpRequest<{ contentId: string; message: string }>(
      'POST',
      `/arches/social-media/inspiration/sources/manual${holderQs(holder)}`,
      data,
    );
  },

  generatePostDraft(holder: string, contentId: string) {
    return api.httpRequest<{ postId: string }>(
      'POST',
      `/arches/social-media/inspiration/${contentId}/generate-draft${holderQs(holder)}`,
    );
  },

  // ── Brand settings ──────────────────────────────────────────────────

  getBrandSettings(holder: string) {
    return api.httpRequest<BrandSettingsPayload>(
      'GET',
      `/arches/social-media/brand-settings${holderQs(holder)}`,
    );
  },

  upsertBrandSettings(
    holder: string,
    data: {
      identity?: Partial<{
        brandName: string;
        industry: string | null;
        tagline: string | null;
        website: string | null;
      }>;
      logo?: Partial<{
        lightLogoFileId: string | null;
        darkLogoFileId: string | null;
        usage: string;
        placement: string;
      }>;
      palette?: Array<{ name: string; hex: string }>;
      voice?: Partial<{
        personaArchetype: string;
        toneSpectrum: { formality: number; humor: number; boldness: number; empathy: number };
        voiceDescriptors: string[];
        avoidDescriptors: string[];
        brandDescription: string | null;
        targetAudience: string | null;
        contentPillars: string | null;
        personaVoice: string | null;
        signOffStyle: string;
        defaultCTAType: string;
        wordsToUse: string | null;
        wordsToNeverUse: string | null;
        brandSpecificTerms: string | null;
        hashtagStrategy: string | null;
        emojiUsage: string;
        postLengthPreference: string;
        useLineBreaks: boolean;
        includeQuestions: boolean;
        referenceBrandColor: boolean;
      }>;
      platformOverrides?: Array<{
        platform: string;
        toneNote: string | null;
        isActive: boolean;
      }>;
    },
  ) {
    return api.httpRequest<BrandSettingsPayload>(
      'PUT',
      `/arches/social-media/brand-settings${holderQs(holder)}`,
      data,
    );
  },
};

export type { BrandSettingsPayload };
